import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, fmtDate } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Loader2, Search, Inbox } from "lucide-react";
import { toast } from "sonner";

export default function GenericModule({ config }) {
  const { hasPerm } = useAuth();
  const canManage = hasPerm(`${config.module}:manage`);
  const [rows, setRows] = useState(null);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(config.endpoint);
      setRows(data);
    } catch (e) { toast.error(apiError(e)); setRows([]); }
  }, [config.endpoint]);

  useEffect(() => { setRows(null); load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm({}); setDialogOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm({ ...row }); setDialogOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.put(`${config.endpoint}/${editing.id}`, form);
      else await api.post(config.endpoint, form);
      toast.success(editing ? "Updated" : "Created");
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`${config.endpoint}/${deleteTarget.id}`);
      toast.success("Deleted");
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const filtered = (rows || []).filter((r) =>
    !search || JSON.stringify(r).toLowerCase().includes(search.toLowerCase())
  );

  const renderCell = (row, col) => {
    const v = row[col.key];
    if (col.type === "badge") return <StatusBadge value={v} />;
    if (col.type === "date") return <span className="text-muted-foreground">{fmtDate(v)}</span>;
    if (v === undefined || v === null || v === "") return <span className="text-muted-foreground">—</span>;
    return <span className="truncate block max-w-[220px]">{String(v)}</span>;
  };

  return (
    <div data-testid={`module-${config.path}`}>
      <PageHeader
        title={config.title}
        subtitle={config.subtitle}
        actions={canManage && (
          <Button onClick={openCreate} className="rounded-md" data-testid={`${config.path}-add-btn`}>
            <Plus className="w-4 h-4 mr-1.5" /> Add
          </Button>
        )}
      />

      <div className="relative mb-4 max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…"
          className="pl-9 bg-card" data-testid={`${config.path}-search`} />
      </div>

      <div className="border border-border rounded-lg bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {config.columns.map((c) => (
                  <TableHead key={c.key} className="text-xs uppercase tracking-wider">{c.label}</TableHead>
                ))}
                {canManage && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows === null ? (
                <TableRow><TableCell colSpan={99} className="text-center py-12"><Loader2 className="w-5 h-5 animate-spin mx-auto text-primary" /></TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={99} className="text-center py-12 text-muted-foreground">
                  <Inbox className="w-6 h-6 mx-auto mb-2 opacity-50" /> No records found.
                </TableCell></TableRow>
              ) : filtered.map((row) => (
                <TableRow key={row.id} data-testid={`${config.path}-row`}>
                  {config.columns.map((c) => (
                    <TableCell key={c.key} className="py-2.5">{renderCell(row, c)}</TableCell>
                  ))}
                  {canManage && (
                    <TableCell className="text-right py-2">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(row)} data-testid={`${config.path}-edit`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-danger" onClick={() => setDeleteTarget(row)} data-testid={`${config.path}-delete`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Create / Edit */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} {config.title}</DialogTitle></DialogHeader>
          <div className="space-y-3.5 py-1">
            {config.fields.map((f) => (
              <div key={f.key} className="space-y-1.5">
                <Label>{f.label}{f.required && <span className="text-danger"> *</span>}</Label>
                {f.type === "textarea" ? (
                  <Textarea value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    data-testid={`field-${f.key}`} />
                ) : f.type === "select" ? (
                  <Select value={form[f.key] || ""} onValueChange={(v) => setForm({ ...form, [f.key]: v })}>
                    <SelectTrigger data-testid={`field-${f.key}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      {f.options.map((o) => <SelectItem key={o} value={o}>{o.replace(/_/g, " ")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input type={f.type === "number" ? "number" : "text"} value={form[f.key] ?? ""}
                    onChange={(e) => setForm({ ...form, [f.key]: f.type === "number" ? Number(e.target.value) : e.target.value })}
                    data-testid={`field-${f.key}`} />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid={`${config.path}-save`}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this record?</AlertDialogTitle>
            <AlertDialogDescription>This action can be undone by an administrator. Continue?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-danger hover:bg-danger/90" data-testid="confirm-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
